import { ConfirmDialogProps, confirmDialogState } from "@mirage/components/confirmActionDialog/ConfirmActionDialog";
import { useCallback, useRef } from "react";
import { useSetRecoilState } from "recoil";

export const useConfirmDialog = (): UseConfirmDialogReturn => {
    const setState = useSetRecoilState(confirmDialogState);
    const hideDialog = useCallback(
        () =>
            setState((prev) => {
                return { ...prev, dialogActions: undefined, open: false, onConfirmColor: undefined, onConfirmDisabled: false };
            }),
        [setState]
    );
    const onConfirm = useRef<VoidFunction>(hideDialog);
    const onReject = useRef<VoidFunction>(hideDialog);

    const isConfirmed = useCallback(() => {
        return new Promise<boolean>((resolve) => {
            onConfirm.current = () => {
                resolve(true);
                hideDialog();
                onConfirm.current = hideDialog;
            };
            onReject.current = () => {
                resolve(false);
                hideDialog();
                onReject.current = hideDialog;
            };
            setState((prev) => {
                return {
                    ...prev,
                    onConfirm: onConfirm.current,
                    onReject: onReject.current,
                };
            });
        });
    }, [hideDialog, setState]);

    const showDialog = useCallback(
        (props: Omit<ConfirmDialogProps, "onReject" | "onConfirm" | "open">) => {
            setState(() => {
                return {
                    ...props,
                    open: true,
                    onConfirm: onConfirm.current,
                    onReject: onReject.current,
                };
            });
            return isConfirmed();
        },
        [setState]
    );

    const changeState = useCallback(
        ({ onConfirmDisabled }: Pick<ConfirmDialogProps, "onConfirmDisabled">) => {
            setState((prev) => {
                return { ...prev, onConfirmDisabled };
            });
        },
        [setState]
    );

    return [showDialog, isConfirmed, changeState];
};

type UseConfirmDialogReturn = [
    (props: Omit<ConfirmDialogProps, "onReject" | "onConfirm" | "open">) => Promise<boolean>,
    () => Promise<boolean>,
    ({ onConfirmDisabled }: Pick<ConfirmDialogProps, "onConfirmDisabled">) => void
];
